// @file Vuex Store module for Share panels and subpanels
import { CONFIRM_DISCARD_CHANGES } from 'bits/confirmation_dialog'
import { WAVING_HAND_EMOJI } from 'bits/emoji'
import { __ } from 'bits/intl'
import { buildUrlFromPath, navigateTo } from 'bits/location'
import { OzConfirmationDialogBoxButtonScheme } from 'library/v4/components/OzConfirmationDialogBox.vue'
import PadletApi from 'surface/padlet_api'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

enum ShareSubpanel {
  Main = 'main',
  Lti = 'ltiSettings',
  Embed = 'embed',
  Privacy = 'privacy',
  Members = 'members',
}
interface SharePanelState {
  activePanel: null | ShareSubpanel
  isQrModalOpen: boolean
}

const ShareModule: Module<SharePanelState, RootState> = {
  namespaced: true,
  state: {
    activePanel: null,
    isQrModalOpen: false,
  },
  getters: {
    isOpen(state): boolean {
      return state.activePanel !== null
    },
    isQrModalOpen(state): boolean {
      return state.isQrModalOpen
    },
    activePanel(state): null | ShareSubpanel {
      return state.activePanel
    },
  },
  mutations: {
    HIDE_SHARE_PANEL(state): void {
      state.activePanel = null
      state.isQrModalOpen = false
    },
    SHOW_SHARE_PANEL(state, panel: ShareSubpanel): void {
      state.activePanel = panel
    },
    OPEN_QR_MODAL(state): void {
      state.isQrModalOpen = true
    },
    CLOSE_QR_MODAL(state): void {
      state.isQrModalOpen = false
    },
  },
  actions: {
    showSharePanel({ commit }): void {
      commit('SHOW_SHARE_PANEL', ShareSubpanel.Main)
    },
    hideSharePanel({ commit }): void {
      commit('HIDE_SHARE_PANEL')
    },
    resetSharePanel({ dispatch, commit }): void {
      commit('HIDE_SHARE_PANEL')
      dispatch('wallAccessSettings/emptyWallAccessSettings', null, { root: true })
    },
    confirmResetSharePanel({ dispatch, getters, rootGetters }): void {
      if (
        getters.activePanel === ShareSubpanel.Members
          ? rootGetters['wallAccessSettings/areCollaboratorsDirty']
          : rootGetters['wallAccessSettings/areBaseWallAccessSettingsDirty']
      ) {
        const options = {
          ...CONFIRM_DISCARD_CHANGES,
          afterConfirmActions: [{ type: 'share/hideSharePanel' }],
          xShadow: true,
        }
        dispatch('confirmationDialog/openConfirmationDialog', options, { root: true })
      } else {
        dispatch('hideSharePanel')
      }
    },
    changeActivePanel({ commit }, panel: ShareSubpanel): void {
      commit('SHOW_SHARE_PANEL', panel)
    },
    confirmChangePanelDiscardChanges({ dispatch, getters, rootGetters }, panel: ShareSubpanel): void {
      if (
        getters.activePanel === ShareSubpanel.Members
          ? rootGetters['wallAccessSettings/areCollaboratorsDirty']
          : rootGetters['wallAccessSettings/areBaseWallAccessSettingsDirty']
      ) {
        const options = {
          ...CONFIRM_DISCARD_CHANGES,
          afterConfirmActions: [
            { type: 'share/changeActivePanel', payload: panel },
            { type: 'wallAccessSettings/discardChanges' },
          ],
          xShadow: true,
        }
        dispatch('confirmationDialog/openConfirmationDialog', options, { root: true })
      } else {
        dispatch('changeActivePanel', panel)
      }
    },
    toggleSharePanel({ commit, getters }, panel: ShareSubpanel): void {
      if (getters.isOpen) {
        commit('HIDE_SHARE_PANEL')
      } else {
        commit('SHOW_SHARE_PANEL', panel)
      }
    },
    openQrModal({ commit }): void {
      commit('OPEN_QR_MODAL')
    },
    closeQrModal({ commit }): void {
      commit('CLOSE_QR_MODAL')
    },
    confirmCollaboratorLeavePadlet({ rootGetters, dispatch }): void {
      const adminOrAdministrator = rootGetters.isLibraryWall === true ? 'admin' : 'administrator'
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...WAVING_HAND_EMOJI,
          isCodeProtected: false,
          title: __('Leave this padlet?'),
          body: __('You will have to be invited back by an %{adminOrAdministrator}.', { adminOrAdministrator }),
          confirmButtonText: __('Leave'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [{ type: 'share/leavePadlet' }],
          buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
          xShadow: true,
        },
        { root: true },
      )
    },
    async leavePadlet({ dispatch, rootGetters }): Promise<void> {
      try {
        await PadletApi.collaboratorLeavePadlet(rootGetters['wallId'])
        const dashboardUrl = buildUrlFromPath('dashboard')
        navigateTo(dashboardUrl)
      } catch (e) {
        dispatch(
          'confirmationDialog/openConfirmationDialog',
          {
            ...WAVING_HAND_EMOJI,
            isCodeProtected: false,
            title: __('Oops!'),
            body: __('Something went wrong. We were unable to process your leave request.'),
            confirmButtonText: __('Try again'),
            cancelButtonText: __('Nevermind'),
            afterConfirmActions: [{ type: 'share/leavePadlet' }],
            buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
          },
          { root: true },
        )
      }
    },
  },
}

export default ShareModule
export { ShareSubpanel, SharePanelState }
